import { Box, Button, Divider, Slide, Typography } from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SearchIcon from '@mui/icons-material/Search';
import PlaceIcon from '@mui/icons-material/Place';

import RedirectButton from '../../../common/component/parts/RedirectButton';
import RedirectMenu from '../../../common/component/parts/RedirectMenu';
import { content, pageTitle, main, width, searchButton } from '../../../common/const/componentSize';
import { buttonName, coexistenceGetType, g500LiteGetType, header, offsegGetType } from '../../../common/const/commonConst';
import { DialogType } from '../../../common/type/DialogType';
import { useStateOffsegMenu } from '../../../common/component/hooks/useStateOffsegMenu';
import { useStateG500LiteMenu } from '../../../common/component/hooks/useStateG500LiteMenu';
import { useEffectMasterInfo } from '../../../common/component/hooks/useEffectMasterInfo';
import { useStateSelectedOffsegMasterInfo } from '../../../common/component/hooks/useStateSelectedOffsegMasterInfo';
import { useStateSelectedG500LiteMasterInfo } from '../../../common/component/hooks/useStateSelectedG500LiteMasterInfo';
import { useEffectOffsegMasterInfo } from '../../../common/component/hooks/useEffectOffsegMasterInfo';
import { useEffectG500LiteMasterInfo } from '../../../common/component/hooks/useEffectG500LiteMasterInfo';

import { useStateSideBar } from '../hooks/useStateSideBar';
import { useStateUpdatedDatetime } from '../hooks/useStateUpdatedDatetime';
import { useStateCenterCoordinates } from '../hooks/useStateCenterCoordinates';
import { useStateCurrentCarPlace } from '../hooks/useStateCurrentCarPlace';
import { useGetCurrentCarPlace } from '../hooks/useGetCurrentCarPlace';
import { CurrentCarPlaceSearch } from '../parts/CurrentCarPlaceSearch';
import { CurrentCarPlaceTable } from '../parts/CurrentCarPlaceTable';
import CurrentCarPlaceMap from '../parts/CurrentCarPlaceMap';
import { StyledIconButton } from '../styled/IconButtonStyled';
import { exampleLabel, g500LiteMenuItemList, lastUpdatedDatetimeName, offsegMenuItemList, pageName } from '../../const/currentCarPlacePageConst';
import { exampleBox, search, sideBar, sideBarOpenButton } from '../../const/currentCarPlacePageSize';

import theme from '../../../theme';

type CurrentCarPlacePagePropsType = {
	setIsAsyncProcess: React.Dispatch<React.SetStateAction<boolean>>;
	setDialog: React.Dispatch<React.SetStateAction<DialogType>>;
};

/**
 * 車両現在位置一覧画面
 */
export const CurrentCarPlacePage = (props: CurrentCarPlacePagePropsType): JSX.Element => {
	const { setIsAsyncProcess, setDialog } = props;

	const { anchorElOffsegMenu, openOffsegMenu, handleOpenOffsegMenu, handleCloseOffsegMenu } = useStateOffsegMenu(); // Offseg遷移メニューの状態管理用カスタムフック
	const { anchorElG500LiteMenu, openG500LiteMenu, handleOpenG500LiteMenu, handleCloseG500LiteMenu } = useStateG500LiteMenu(); // G500Lite遷移メニューの状態管理用カスタムフック
	const { isSideBarOpened, toggleSideBar } = useStateSideBar(); // サイドバーの開閉状態管理用カスタムフック
	const { lastUpdatedDatetime, setLastUpdatedDatetime } = useStateUpdatedDatetime(); // 更新日時の状態管理用カスタムフック
	const { centeredCurrentCarPlaceInfo, setCenteredCurrentCarPlaceInfo } = useStateCenterCoordinates(); // 地図中央に表示するデータの状態管理用カスタムフック

	const { masterInfo, setMasterInfo } = useEffectMasterInfo(coexistenceGetType.currentCarPlace, setDialog); // マスタデータの状態管理用カスタムフック
	const { selectedOffsegMasterInfo, setSelectedOffsegMasterInfo, onChangeOffsegOrganization, onChangeOffsegCar, onChangeOffsegDriver } =
		useStateSelectedOffsegMasterInfo(); // Offsegの選択したマスタデータの状態管理用カスタムフック
	const { selectedG500LiteMasterInfo, setSelectedG500LiteMasterInfo, onChangeG500LiteOrganization, onChangeG500LiteCar } =
		useStateSelectedG500LiteMasterInfo(); // G500Liteの選択したマスタデータの状態管理用カスタムフック

	const useEffectOffsegMasterInfoInput = {
		getType: offsegGetType.carAndDriverInfo,
		selectedOffsegMasterInfo,
		setSelectedOffsegMasterInfo,
		masterInfo,
		setMasterInfo,
		setDialog,
	};
	useEffectOffsegMasterInfo(useEffectOffsegMasterInfoInput); // Offsegマスタデータの状態管理用カスタムフック

	const useEffectG500LiteMasterInfoInput = {
		getType: g500LiteGetType.carInfo,
		selectedG500LiteMasterInfo,
		setSelectedG500LiteMasterInfo,
		masterInfo,
		setMasterInfo,
		setDialog,
	};
	useEffectG500LiteMasterInfo(useEffectG500LiteMasterInfoInput); // G500Liteマスタデータの状態管理用カスタムフック

	const { currentCarPlaceList, setCurrentCarPlaceList } = useStateCurrentCarPlace(); // 車両現在位置一覧の状態管理用カスタムフック

	const useGetCurrentCarPlaceInput = {
		setIsAsyncProcess,
		selectedOffsegMasterInfo,
		selectedG500LiteMasterInfo,
		setDialog,
		setCurrentCarPlaceList,
		setLastUpdatedDatetime,
		setCenteredCurrentCarPlaceInfo,
	};
	const { onClickSearch } = useGetCurrentCarPlace(useGetCurrentCarPlaceInput); // 車両現在位置一覧取得用カスタムフック

	return (
		<Box
			id="currentCarPlacePage"
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'flex-start',
				position: 'absolute',
				top: pageTitle.top,
				height: main.height,
				width: width,
			}}
		>
			<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', height: pageTitle.height, width: width }}>
				{/* ページタイトル */}
				<Box sx={{ minWidth: sideBar.width }}>
					<Typography id="pageTitle" role="lable" aria-label={pageName} sx={{ fontSize: 25, fontWeight: 'bold', color: theme.palette.common.black }}>
						&nbsp;&nbsp;{pageName}
					</Typography>
				</Box>
				{/* 遷移ボタン */}
				<RedirectButton handleOpenOffsegMenu={handleOpenOffsegMenu} handleOpenG500LiteMenu={handleOpenG500LiteMenu} />
				{/* 更新日時 */}
				<Typography
					noWrap
					role="lable"
					aria-label={lastUpdatedDatetimeName}
					sx={{ fontSize: 18, color: theme.palette.common.black, marginRight: 3 }}
				>{`${lastUpdatedDatetimeName}${lastUpdatedDatetime}`}</Typography>
				{/* 更新ボタン */}
				<Button id="updateButton" variant="contained" onClick={onClickSearch} sx={{ width: searchButton.width, marginRight: 3 }}>
					<Typography sx={{ fontSize: 15, color: theme.palette.common.white }}>{buttonName.update}</Typography>
				</Button>
			</Box>
			{/* 遷移メニュー */}
			<RedirectMenu
				anchorElOffsegMenu={anchorElOffsegMenu}
				anchorElG500LiteMenu={anchorElG500LiteMenu}
				openOffsegMenu={openOffsegMenu}
				openG500LiteMenu={openG500LiteMenu}
				handleCloseOffsegMenu={handleCloseOffsegMenu}
				handleCloseG500LiteMenu={handleCloseG500LiteMenu}
				offsegMenuItemList={offsegMenuItemList}
				g500LiteMenuItemList={g500LiteMenuItemList}
			/>
			{/* サイドバーをopenするボタン */}
			<Box
				sx={{
					position: 'absolute',
					top: sideBarOpenButton.top,
					bgcolor: theme.palette.common.white,
					boxShadow: 10,
				}}
				zIndex={theme.zIndex.drawer}
			>
				<StyledIconButton onClick={toggleSideBar}>
					<KeyboardArrowRightIcon sx={{ fontSize: 30, color: theme.palette.common.black }} />
				</StyledIconButton>
			</Box>
			{/* サイドバー(検索・検索結果のリスト表示) */}
			<Slide direction="right" in={isSideBarOpened} appear={false}>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						position: 'absolute',
						top: content.top,
						height: content.height,
						width: sideBar.width,
						background: theme.palette.background.paper,
					}}
					zIndex={theme.zIndex.drawer}
				>
					{/* 検索表示 */}
					<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', height: search.titleHeight, marginLeft: search.titleMarginLeft }}>
						<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flexGrow: 1, width: search.titleWidth }}>
							<SearchIcon sx={{ fontSize: 27, color: theme.palette.common.black }} />
							<Typography role="lable" aria-label={header.search} sx={{ fontSize: 20, color: theme.palette.common.black }}>
								{header.search}
							</Typography>
						</Box>
						<StyledIconButton onClick={toggleSideBar}>
							<KeyboardArrowLeftIcon sx={{ fontSize: 25, color: theme.palette.common.black }} />
						</StyledIconButton>
					</Box>
					<Divider flexItem />
					<Box sx={{ marginBottom: 2 }}>
						<CurrentCarPlaceSearch
							masterInfo={masterInfo}
							selectedOffsegMasterInfo={selectedOffsegMasterInfo}
							setSelectedOffsegMasterInfo={setSelectedOffsegMasterInfo}
							selectedG500LiteMasterInfo={selectedG500LiteMasterInfo}
							onChangeOffsegOrganization={onChangeOffsegOrganization}
							onChangeOffsegCar={onChangeOffsegCar}
							onChangeOffsegDriver={onChangeOffsegDriver}
							onChangeG500LiteOrganization={onChangeG500LiteOrganization}
							onChangeG500LiteCar={onChangeG500LiteCar}
							onClickSearch={onClickSearch}
						/>
					</Box>
					<Divider flexItem />
					<Box sx={{ marginTop: 2 }}>
						<CurrentCarPlaceTable currentCarPlaceInfoList={currentCarPlaceList} setCenteredCurrentCarPlaceInfo={setCenteredCurrentCarPlaceInfo} />
					</Box>
				</Box>
			</Slide>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'flex-start',
					justifyContent: 'center',
					position: 'absolute',
					top: exampleBox.top,
					right: exampleBox.right,
					height: exampleBox.height,
					width: exampleBox.width,
					borderRadius: exampleBox.borderRadius,
					boxShadow: exampleBox.boxShadow,
					bgcolor: theme.palette.common.white,
				}}
				zIndex={theme.zIndex.appBar}
			>
				<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginLeft: 1 }}>
					<PlaceIcon sx={{ color: theme.palette.secondary.light }} />
					<Typography role="lable" aria-label={exampleLabel.connected} sx={{ fontSize: 15, color: theme.palette.common.black }}>
						{exampleLabel.connected}
					</Typography>
				</Box>
				<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginLeft: 1 }}>
					<PlaceIcon sx={{ color: theme.palette.common.black }} />
					<Typography role="lable" aria-label={exampleLabel.disconnected} sx={{ fontSize: 15, color: theme.palette.common.black }}>
						{exampleLabel.disconnected}
					</Typography>
				</Box>
			</Box>
			{/* 地図表示 */}
			<Box sx={{ position: 'absolute', top: content.top }}>
				<CurrentCarPlaceMap currentCarPlaceInfoList={currentCarPlaceList} centeredCurrentCarPlaceInfo={centeredCurrentCarPlaceInfo} />
			</Box>
		</Box>
	);
};
